import React, { useState, useEffect } from 'react';
import {
  SparklesIcon,
  BriefcaseIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { getTalentPool, getJobRecommendations } from '../services/api';

const Recommendations = () => {
  // State
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Fetch candidates on mount
  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const response = await getTalentPool();
      setCandidates(response.data.candidates || []);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError('Failed to load candidates. Please try again.');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleGetRecommendations = async () => {
    if (!selectedCandidate) {
      setError('Please select a candidate first');
      return;
    }

    setLoading(true);
    setError(null);
    setRecommendations([]);

    try {
      const response = await getJobRecommendations(selectedCandidate._id);
      if (response.data.success) {
        setRecommendations(response.data.recommendations || []);
        if (response.data.recommendations?.length === 0) {
          setError('No matching jobs found. Try adding more open jobs to the system.');
        }
      } else {
        setError(response.data.message || 'Failed to get recommendations');
      }
    } catch (err) {
      console.error('Error getting recommendations:', err);
      setError(err.response?.data?.message || 'Failed to get job recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (score >= 40) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Moderate Match';
    return 'Weak Match';
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <SparklesIcon className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Recommendations</h1>
            <p className="mt-1 text-sm text-gray-500">
              AI-powered job matching for your talent pool candidates
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Candidate Selection Card */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select a Candidate</h2>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Candidate Dropdown */}
            <div className="relative flex-1">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                disabled={candidatesLoading}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              >
                {candidatesLoading ? (
                  <span className="text-gray-400 flex items-center gap-2">
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Loading candidates...
                  </span>
                ) : selectedCandidate ? (
                  <div className="flex items-center gap-3">
                    <UserCircleIcon className="h-8 w-8 text-indigo-500" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{selectedCandidate.fullName}</p>
                      <p className="text-sm text-gray-500">{selectedCandidate.domain || 'No domain specified'}</p>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-500">Choose a candidate from your talent pool...</span>
                )}
                <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && !candidatesLoading && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                  {candidates.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <UserCircleIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>No candidates in talent pool</p>
                      <p className="text-sm mt-1">Add candidates first to get recommendations</p>
                    </div>
                  ) : (
                    candidates.map((candidate) => (
                      <button
                        key={candidate._id}
                        onClick={() => {
                          setSelectedCandidate(candidate);
                          setDropdownOpen(false);
                          setRecommendations([]);
                          setError(null);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                          selectedCandidate?._id === candidate._id ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <UserCircleIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                        <div className="text-left flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{candidate.fullName}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {candidate.domain || 'No domain'} • {candidate.skills?.slice(0, 3).join(', ') || 'No skills listed'}
                          </p>
                        </div>
                        {selectedCandidate?._id === candidate._id && (
                          <CheckBadgeIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Get Recommendations Button */}
            <button
              onClick={handleGetRecommendations}
              disabled={!selectedCandidate || loading}
              className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                !selectedCandidate || loading
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
              }`}
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-5 w-5" />
                  Get Job Recommendations
                </>
              )}
            </button>
          </div>
        </div>

        {/* Selected Candidate Info */}
        {selectedCandidate && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserCircleIcon className="h-6 w-6 text-indigo-600" />
              Candidate Profile
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{selectedCandidate.fullName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Domain</p>
                <p className="font-medium text-gray-900">{selectedCandidate.domain || 'Not classified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium text-gray-900">{selectedCandidate.location || 'Not specified'}</p>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <p className="text-sm text-gray-500 mb-2">Skills</p>
                <div className="flex flex-wrap gap-2">
                  {selectedCandidate.skills?.length > 0 ? (
                    selectedCandidate.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium"
                      >
                        <TagIcon className="h-3.5 w-3.5" />
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 italic">No skills listed</span>
                  )}
                </div>
              </div>
              {selectedCandidate.summary && (
                <div className="md:col-span-2 lg:col-span-3">
                  <p className="text-sm text-gray-500 mb-1">Summary</p>
                  <p className="text-gray-700">{selectedCandidate.summary}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="h-16 w-16 rounded-full border-4 border-indigo-100"></div>
              <div className="absolute top-0 h-16 w-16 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Analyzing candidate profile...</p>
            <p className="text-sm text-gray-400">Finding the best job matches with AI</p>
          </div>
        )}

        {/* Recommendations Results */}
        {!loading && recommendations.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BriefcaseIcon className="h-6 w-6 text-indigo-600" />
              Top Job Matches
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({recommendations.length} found)
              </span>
            </h3>

            <div className="grid gap-4">
              {recommendations.map((job, index) => (
                <div
                  key={job.jobId}
                  className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Job Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <span className="text-xl font-bold text-indigo-600">#{index + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-lg font-semibold text-gray-900">{job.title}</h4>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <BuildingOfficeIcon className="h-4 w-4" />
                              {job.company || 'Company not specified'}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPinIcon className="h-4 w-4" />
                              {job.location || 'Location not specified'}
                            </span>
                            {job.type && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">
                                {job.type}
                              </span>
                            )}
                            {job.industry && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-600 text-xs">
                                {job.industry}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Reason */}
                      <div className="mt-4 bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Why this is a match:</p>
                        <p className="text-gray-600">{job.reason}</p>
                      </div>
                    </div>

                    {/* Match Score */}
                    <div className="lg:w-48 flex-shrink-0">
                      <div className={`rounded-lg p-4 text-center border ${getScoreBadgeColor(job.matchScore)}`}>
                        <p className="text-3xl font-bold">{job.matchScore}%</p>
                        <p className="text-sm font-medium mt-1">{getScoreLabel(job.matchScore)}</p>
                      </div>
                      <div className="mt-3">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getScoreColor(job.matchScore)} transition-all duration-500`}
                            style={{ width: `${job.matchScore}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State (after search with no results) */}
        {!loading && selectedCandidate && recommendations.length === 0 && !error && (
          <div className="text-center py-16">
            <BriefcaseIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No recommendations yet</h3>
            <p className="text-gray-500 mt-1">
              Click "Get Job Recommendations" to find matching jobs for this candidate
            </p>
          </div>
        )}

        {/* Initial State */}
        {!loading && !selectedCandidate && recommendations.length === 0 && (
          <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <SparklesIcon className="h-16 w-16 text-indigo-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Ready to Find Perfect Matches</h3>
            <p className="text-gray-500 mt-1 max-w-md mx-auto">
              Select a candidate from your talent pool above, then click "Get Job Recommendations"
              to discover the top matching jobs using AI analysis.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recommendations;
